import { Button, Grid, Paper, Theme, useTheme } from "@mui/material";
import countries from "iso3166-2-db/countryList/dispute/UN/de.json";
import { ValidationErrors } from "final-form";
import { OptionHTMLAttributes, useState } from "react";
import { Form } from "react-final-form";
import { connect, ConnectedProps } from "react-redux";
import { Navigate } from "react-router-dom";
import { AnyAction, bindActionCreators, Dispatch } from "redux";
import { UrlPathOperationalUnit } from "../../api/url";
import { thunkCreateOperationalUnit, thunkUpdateOperationalUnit } from "../../store/actions/OperationalUnitActions";
import { IStore } from "../../store/IStore";
import { OperationalUnitDto } from "../../store/models/operationalUnit/OperationalUnitDto";
import { OperationalUnitType } from "../../store/models/operationalUnit/OperationalUnitType";
import { FormInput } from "../atoms/FormInput";
import { FormSelect } from "../atoms/FormSelect";
import ImsPaperHead from "../atoms/ImsPaperHead";
import { getEuropeanCountries, isBlank } from "../atoms/Utils";
import { useTranslation } from "react-i18next";
import { useValidateEmail } from "../atoms/FieldValidation";

interface OperationalUnitFormProps {
  operationalUnit: OperationalUnitDto;
}

const countryList = getEuropeanCountries(countries);

export const getOperationalUnitOptions = (
  operationalUnit: OperationalUnitDto | undefined,
  operationalUnits: OperationalUnitDto[],
  theme: Theme,
  required: boolean,
  externalUnitNumber: boolean
): OptionHTMLAttributes<HTMLOptionElement>[] => {
  const rootOperationalUnit = operationalUnits.find((operationalUnit) => !operationalUnit.parentUnitNumber)!;
  const hierarchy = getSubUnitHierarchy(operationalUnit, rootOperationalUnit, operationalUnits, 0);
  const result = hierarchy.map((operationalUnit) => ({
    value: externalUnitNumber ? operationalUnit.externalUnitNumber : operationalUnit.unitNumber,
    label: operationalUnit.unitName,
    style: {
      paddingLeft: theme.spacing(operationalUnit.depth * 4 + 2),
    },
    tabIndex: operationalUnit.depth,
  }));
  return required ? result : [{ value: "", label: "---" }].concat(result);
};

const getSubUnitHierarchy = (
  operationalUnit: OperationalUnitDto | undefined,
  rootOperationalUnit: OperationalUnitDto,
  operationalUnits: OperationalUnitDto[],
  depth: number
): (OperationalUnitDto & { depth: number })[] => {
  if (rootOperationalUnit === operationalUnit) return [];
  const subUnits = operationalUnits.filter((unit) => unit.parentUnitNumber === rootOperationalUnit.unitNumber);
  let result = [{ ...rootOperationalUnit, depth }];
  subUnits.forEach((subUnit) => {
    const subUnitHierarchy = getSubUnitHierarchy(operationalUnit, subUnit, operationalUnits, depth + 1);
    result = result.concat(subUnitHierarchy);
  });
  return result;
};

const OperationalUnitForm = (props: OperationalUnitFormProps & ThunkProps) => {
  const theme = useTheme();
  const [redirect, setRedirect] = useState(false);
  const { t } = useTranslation();
  const validateEmail = useValidateEmail();

  const unitTypeOptions = [
    { value: OperationalUnitType.TRADE_ASSOCIATION, label: t("operationalUnit.company") },
    { value: OperationalUnitType.COUNTRY, label: t("operationalUnit.countryUnit") },
    { value: OperationalUnitType.REGION, label: t("operationalUnit.region") },
    { value: OperationalUnitType.DEPARTMENT, label: t("operationalUnit.department") },
    { value: OperationalUnitType.RETAILER, label: t("operationalUnit.retailers") },
    { value: OperationalUnitType.BRANCH_STORE, label: t("operationalUnit.operationalUnitDropdown") },
    { value: OperationalUnitType.CASH_TILL, label: t("operationalUnit.cashRegister") },
    { value: OperationalUnitType.BUSINESS_UNIT, label: t("operationalUnit.businessUnit") },
  ];
  
  const saveOperationalUnit = async (ouToSave: OperationalUnitDto) => {
    let result;
    if (ouToSave.unitNumber) {
      result = await props.thunkUpdateOperationalUnit(ouToSave);
    } else {
      result = await props.thunkCreateOperationalUnit(ouToSave);
    }
    if (result) setRedirect(true);
  };

  const validateForm = (values: OperationalUnitDto) => {
    const errors: ValidationErrors = {
      contact: {
        address: {},
      },
    };
    validateRequiredFields(["unitName", "externalUnitNumber", "unitType"], values, errors);
    if (values.parentUnitNumber && values.unitType === OperationalUnitType.TRADE_ASSOCIATION) {
      errors.unitType = t("operationalUnit.messages.onlyOneCompany");
    }
    if (values.unitType === OperationalUnitType.COUNTRY) {
      const parent = props.operationalUnits.find((u) => u.unitNumber === values.parentUnitNumber);
      if (!parent || parent.unitType !== OperationalUnitType.TRADE_ASSOCIATION) {
        errors.unitType = t("operationalUnit.messages.countryParentMustBeCompany");
      }
    }
    const phoneNumberRegex = new RegExp("^\\+?[\\d\\s]*$");
    if (values.contact?.address) {
      validateRequiredFields(
        ["streetName", "cityName", "zipCode", "country"],
        values.contact?.address,
        errors.contact.address
      );
    }
    if (values.contact?.phoneNumber && !phoneNumberRegex.test(values.contact?.phoneNumber)) {
      errors.contact.phoneNumber = t("operationalUnit.messages.validPhoneNumber");
    }
    validateEmail(values.contact?.mailAddress, errors, "contact.mailAddress");
    return errors;
  };

  const validateRequiredFields = (fields: string[], values: any, errors: any) => {
    fields.forEach((field) => {
      if (isBlank(values[field])) {
        errors[field] = t("messages.validation.required");
      }
    });
  };

  const { operationalUnit, operationalUnits, operationalUnitTypes } = props;
  if (redirect) {
    return <Navigate to={UrlPathOperationalUnit} />;
  }

  if (!operationalUnits.length) return null;

  const operationalUnitOptions = getOperationalUnitOptions(operationalUnit, operationalUnits, theme, true, false);

  const allowedUnitTypeOptions = operationalUnitTypes.map((value) => ({
    value,
    label: unitTypeOptions.find((option) => option.value === value)?.label || value,
  }));
  return (
    <Form
      onSubmit={saveOperationalUnit}
      initialValues={operationalUnit}
      validate={validateForm}
      render={({ handleSubmit, submitting }) => (
        <form onSubmit={handleSubmit}>
          <Paper
            style={{
              padding: theme.spacing(3),
              marginTop: theme.spacing(6),
            }}
          >
            <ImsPaperHead text={t("operationalUnit.headlineDetails")} />
            <Grid container spacing={4}>
              <Grid item container spacing={2}>
                <Grid item md={12}>
                  <b>{t("operationalUnit.labelSuperordinateOu")}</b>
                </Grid>
                <Grid item sm={6}>
                  <FormSelect
                    name="parentUnitNumber"
                    label={t("operationalUnit.operationalUnit")}
                    options={operationalUnitOptions}
                  />
                </Grid>
              </Grid>

              <Grid item container spacing={2}>
                <Grid item md={12}>
                  <b>{t("operationalUnit.labelOperationalUnit")}</b>
                </Grid>
                <Grid item sm={4}>
                  <FormInput
                    variant="outlined"
                    fullWidth={true}
                    type="text"
                    name="unitName"
                    label={t("operationalUnit.name")}
                  />
                </Grid>
                <Grid item sm={3}>
                  <FormInput
                    variant="outlined"
                    fullWidth={true}
                    type="text"
                    name="externalUnitNumber"
                    label={t("operationalUnit.externalId")}
                  />
                </Grid>
                <Grid item sm={3}>
                  <FormSelect
                    name="unitType"
                    fullWidth={true}
                    label={t("operationalUnit.unitType")}
                    options={allowedUnitTypeOptions}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <Paper
            style={{
              padding: theme.spacing(3),
              margin: theme.spacing(6, 0, 3, 0),
            }}
          >
            <ImsPaperHead text={t("operationalUnit.contact")} />
            <Grid container spacing={4}>
              <Grid item container spacing={2}>
                <Grid item md={12}>
                  <b>{t("operationalUnit.contact")}</b>
                </Grid>
                <Grid item sm={4}>
                  <FormInput variant="outlined" fullWidth={true} type="text" name="contact.firstName" label={t("operationalUnit.firstName")} />
                </Grid>
                <Grid item sm={4}>
                  <FormInput variant="outlined" fullWidth={true} type="text" name="contact.lastName" label={t("operationalUnit.surname")} />
                </Grid>
              </Grid>
              <Grid item container spacing={2}>
                <Grid item sm={4}>
                  <FormInput
                    variant="outlined"
                    fullWidth={true}
                    type="text"
                    name="contact.phoneNumber"
                    label={t("operationalUnit.telephone")}
                  />
                </Grid>
                <Grid item sm={4}>
                  <FormInput
                    variant="outlined"
                    fullWidth={true}
                    type="text"
                    name="contact.mailAddress"
                    label={t("operationalUnit.email")}
                  />
                </Grid>
              </Grid>
              <Grid item container spacing={2}>
                <Grid item md={12}>
                  <b>{t("operationalUnit.address")}</b>
                </Grid>
                <Grid item sm={4}>
                  <FormInput
                    variant="outlined"
                    fullWidth={true}
                    type="text"
                    name="contact.address.streetName"
                    label={t("operationalUnit.street")}
                  />
                </Grid>
                <Grid item sm={2}>
                  <FormInput
                    variant="outlined"
                    fullWidth={true}
                    type="text"
                    name="contact.address.houseNumber"
                    label={t("operationalUnit.houseNumber")}
                  />
                </Grid>
              </Grid>
              <Grid item container spacing={2}>
                <Grid item sm={4}>
                  <FormInput
                    variant="outlined"
                    fullWidth={true}
                    type="text"
                    name="contact.address.zipCode"
                    label={t("operationalUnit.postCode")}
                  />
                </Grid>
                <Grid item sm={4}>
                  <FormInput
                    variant="outlined"
                    fullWidth={true}
                    type="text"
                    name="contact.address.cityName"
                    label={t("operationalUnit.city")}
                  />
                </Grid>
              </Grid>
              <Grid item container spacing={2}>
                <Grid item md={4}>
                  <FormSelect name="contact.address.country" label={t("operationalUnit.country")} options={countryList} fullWidth={true} />
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <Grid container justifyContent="flex-end">
            <Button
              color="secondary"
              variant="contained"
              onClick={() => setRedirect(true)}
              style={{ margin: theme.spacing(0, 0, 2, 2) }}
            >
              {t("operationalUnit.buttonCancel")}
            </Button>
            <Button
              color="primary"
              type="submit"
              variant="contained"
              disabled={submitting}
              style={{ margin: theme.spacing(0, 0, 2, 2) }}
            >
              {t("operationalUnit.buttonSave")}
            </Button>
          </Grid>
        </form>
      )}
    />
  );
};

const mapStateToProps = (state: IStore) => ({
  operationalUnits: state.operationalUnits.operationalUnits,
  operationalUnitTypes: state.operationalUnits.operationalUnitTypes,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      thunkUpdateOperationalUnit,
      thunkCreateOperationalUnit,
    },
    dispatch
  );

const connector = connect(mapStateToProps, mapDispatchToProps);
type ThunkProps = ConnectedProps<typeof connector>;
export default connector(OperationalUnitForm);
